/**
 * useTipJarMutationObserver Hook
 * 
 * Monitors the SystemMenu DOM container for changes and injects TipJar if absent.
 * Uses MutationObserver to detect when SystemMenu is added/modified.
 * Ensures TipJar adheres to existing SystemMenu flex/grid positioning.
 * 
 * Usage:
 *   useTipJarMutationObserver();
 */

import { useEffect, useRef } from 'react';
import { createTipJarElement } from '@/components/TipJar';

export function useTipJarMutationObserver(): void {
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    // Initialize MutationObserver
    observerRef.current = new MutationObserver(() => {
      checkAndInjectTipJar();
    });

    // Start observing the document body for DOM changes
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-component'],
    });

    // Initial check
    checkAndInjectTipJar();

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
}

/**
 * Check if TipJar exists in SystemMenu, inject if missing
 */
function checkAndInjectTipJar(): void {
  try {
    // Check if TipJar already exists
    if (document.getElementById('tip-jar-container')) {
      return;
    }

    // Find the SystemMenu modal content
    const modalContent = document.querySelector('[class*="modalContent"]');
    if (!modalContent) {
      // SystemMenu not visible yet
      return;
    }

    // Create and inject the TipJar element
    const tipJarElement = createTipJarElement({
      tipJarUrl: 'https://tiptopjar.com/chromedinogame',
    });
    
    modalContent.appendChild(tipJarElement);
    console.log('[useTipJarMutationObserver] TipJar injected into SystemMenu');
  } catch (error) {
    console.error('[useTipJarMutationObserver] Error injecting TipJar:', error);
  }
}
